// One request (03-the-guardrails-service.md § the console: an app's
// escalation reaches the person through the gateway's words as a link to
// this page opened on that escalation): the request in full, with its
// resolution when it has one, and *Approve* and *Deny* while it waits. A
// request this person may not resolve is not found, never forbidden.

import { Link, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { isRefusal } from "../../platform/bus.ts";
import { Empty, PageHead, Pending, useLoad } from "../../ui/components.tsx";
import { Code, validID } from "../../wire/guard.ts";
import { GuardRefusal } from "../guardrails/Unserved.tsx";
import { useSurface } from "../surface.tsx";
import { getAddressed } from "./getAddressed.ts";
import { RequestCard } from "./RequestCard.tsx";

export function ApprovalPage() {
  const { id = "" } = useParams();
  const s = useSurface();
  if (!validID(id)) {
    return (
      <PageHead
        crumb={<Link to={tenantPath(s.tenant, "approvals")}>Approvals</Link>}
        title="Not a request"
        line={`"${id}" is not a request's id.`}
      />
    );
  }
  return <One id={id} />;
}

function One({ id }: { id: string }) {
  const s = useSurface();
  const [load, reload] = useLoad(`approvals/${id}`, () =>
    getAddressed(s.bus, s.principal, s.offered("approvals:admin"), id),
  );
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "approvals")}>Approvals</Link>
      <span>/</span>
      <span className="mono">{id}</span>
    </>
  );
  if (load.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading the request" />
      </>
    );
  }
  if (load.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title="A request" />
        {isRefusal(load.err, Code.notFound) ? (
          <Empty>
            No request by this id waits for you. It may have been decided, run out of time, or be someone
            else&apos;s to decide.
          </Empty>
        ) : (
          <GuardRefusal
            err={load.err}
            what="This request"
            absent="No request can wait for confirmation in this install until the guardrails service runs."
          />
        )}
      </>
    );
  }
  return (
    <>
      <PageHead
        crumb={crumb}
        title="A request waiting for you"
        line="Decide from what was asked, as it was asked."
      />
      <RequestCard record={load.value} full onResolved={reload} />
    </>
  );
}
